import React from 'react';
import * as XLSX from 'xlsx';

const CalibrationVerification = () => {
  const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
  const calibrationData = inspectionData.calibrationVerificationOfSecondaryModule;

  if (!calibrationData) {
    return <p className="text-gray-500">No Calibration Verification data available</p>;
  }

  // Function to download data as Excel
  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [];

    // Headers for the Excel sheet
    worksheetData.push([
      'Line',
      'Date',
      'Serial Number',
      'Test Time',
      'Calibration Verified',
      'Alpha Value',
      'Beta Value',
      'Gamma Value',
      'After Cal - Module ID',
      'After Cal - First Flash',
      'After Cal - Second Flash',
      'After Cal - Third Flash',
      'After Cal - Average Flash',
      'Certified - FF',
      'Certified - IMP',
      'Certified - ISC',
      'Certified - PMAX',
      'Certified - VMP',
      'Certified - VOC',
      'Test - FF',
      'Test - IMP',
      'Test - ISC',
      'Test - PMAX',
      'Test - VMP',
      'Test - VOC',
      'Difference - PMAX',
    ]);

    // Populate data for each line
    Object.entries(calibrationData).forEach(([lineKey, lineData]) => {
      worksheetData.push([
        lineData.line,
        lineData.date,
        lineData.serialNumber,
        lineData.testTime,
        lineData.calibrationVerified,
        lineData.alphaValue,
        lineData.betaValue,
        lineData.gammaValue,
        lineData.afterCalFlashes.moduleId,
        lineData.afterCalFlashes.firstFlash,
        lineData.afterCalFlashes.secondFlash,
        lineData.afterCalFlashes.thirdFlash,
        lineData.afterCalFlashes.averageFlash,
        lineData.certifiedValues.ff,
        lineData.certifiedValues.imp,
        lineData.certifiedValues.isc,
        lineData.certifiedValues.pmax,
        lineData.certifiedValues.vmp,
        lineData.certifiedValues.voc,
        lineData.testValue.ff,
        lineData.testValue.imp,
        lineData.testValue.isc,
        lineData.testValue.pmax,
        lineData.testValue.vmp,
        lineData.testValue.voc,
        lineData.differenceValue.pmax,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'CalibrationVerification');
    XLSX.writeFile(workbook, 'CalibrationVerificationData.xlsx');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Calibration Verification Data</h2>
        <button
          onClick={downloadExcel}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none"
        >
          Download Excel
        </button>
      </div>

      {Object.entries(calibrationData).map(([lineKey, lineData]) => (
        <div key={lineKey} className="border-b pb-4">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {lineData.line} - {lineData.date}
          </h3>

          {/* Scalar Fields */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <p><strong>Serial Number:</strong> {lineData.serialNumber || 'N/A'}</p>
              <p><strong>Test Time:</strong> {lineData.testTime || 'N/A'}</p>
              <p><strong>Calibration Verified:</strong> {lineData.calibrationVerified || 'N/A'}</p>
              <p><strong>Alpha Value:</strong> {lineData.alphaValue || 'N/A'}</p>
              <p><strong>Beta Value:</strong> {lineData.betaValue || 'N/A'}</p>
              <p><strong>Gamma Value:</strong> {lineData.gammaValue || 'N/A'}</p>
            </div>
          </div>

          {/* After Calibration Flashes */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">After Calibration Flashes</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">Module ID</th>
                    <th className="px-4 py-2 text-left text-gray-700">First Flash</th>
                    <th className="px-4 py-2 text-left text-gray-700">Second Flash</th>
                    <th className="px-4 py-2 text-left text-gray-700">Third Flash</th>
                    <th className="px-4 py-2 text-left text-gray-700">Average Flash</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.afterCalFlashes.moduleId || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.afterCalFlashes.firstFlash || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.afterCalFlashes.secondFlash || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.afterCalFlashes.thirdFlash || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.afterCalFlashes.averageFlash || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Certified Values */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Certified Values</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">FF</th>
                    <th className="px-4 py-2 text-left text-gray-700">IMP</th>
                    <th className="px-4 py-2 text-left text-gray-700">ISC</th>
                    <th className="px-4 py-2 text-left text-gray-700">PMAX</th>
                    <th className="px-4 py-2 text-left text-gray-700">VMP</th>
                    <th className="px-4 py-2 text-left text-gray-700">VOC</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.ff || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.imp || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.isc || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.pmax || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.vmp || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.certifiedValues.voc || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Test Values */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Test Values</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">FF</th>
                    <th className="px-4 py-2 text-left text-gray-700">IMP</th>
                    <th className="px-4 py-2 text-left text-gray-700">ISC</th>
                    <th className="px-4 py-2 text-left text-gray-700">PMAX</th>
                    <th className="px-4 py-2 text-left text-gray-700">VMP</th>
                    <th className="px-4 py-2 text-left text-gray-700">VOC</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.ff || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.imp || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.isc || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.pmax || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.vmp || 'N/A'}</td>
                    <td className="px-4 py-2 text-gray-600">{lineData.testValue.voc || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Difference Value */}
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700 mb-2">Difference Value</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-left text-gray-700">PMAX</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-4 py-2 text-gray-600">{lineData.differenceValue.pmax || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CalibrationVerification;